"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defineEntity = void 0;
const zod_1 = require("zod");
const contracts_core_1 = require("@omniflow/contracts-core");
const id_1 = require("./id");
const compose_1 = require("./compose");
const error_codes_1 = require("./error-codes");
const reply_helpers_1 = require("./reply-helpers");
const extract_fields_1 = require("./extract-fields");
const filter_zod_1 = require("./filter-zod");
const aggregate_zod_1 = require("./aggregate-zod");
const projection_zod_1 = require("./projection-zod");
const define_crud_1 = require("./define-crud");
const entity_ops_1 = require("./entity-ops");
const lookup_zod_1 = require("./lookup-zod");
const toPascalCase = (name) => name
    // Split on `-`, `_` и `.` — последнее для dotted entity-имён вида
    // 'spg.provider' (субъекты `cmd.spg.provider.*`), чтобы PascalCase-литерал
    // получался чистым: 'spg.provider' → 'SpgProvider' → SpgProviderCreateCommand.
    .split(/[-_.]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
/**
 * One-call CRUD entity declaration. Takes Domain (business fields only) +
 * entity name, produces Snapshot, Patch, standard CRUD routes and individual
 * zod schemas.
 *
 * Reads (get/list/aggregate) are always present. Write commands and events are
 * declared by `commands`/`events` (default — all three of each); an absent
 * operation is an absent key in `routes` (ROENT, docs/crud-spec.md
 * «Частичный набор команд»). Schemas are built for every operation regardless.
 *
 * Platform fields (id/tenantId/createdAt/updatedAt) are NOT in Domain —
 * they appear only in Snapshot (via composeSnapshot). Patch = Domain.partial()
 * — impossible to accidentally include tenantId.
 *
 * Domain extensions (non-CRUD routes) are added by spreading:
 *   `{ ...entity.routes, attachTrace: defineCommand(...) } as const`
 *
 * @param name Entity name for NATS subjects ('session' → cmd.session.create)
 *   and PascalCase type literals ('Session' → SessionCreateCommand).
 * @param domain Zod schema with ONLY business fields.
 *
 * @example Minimal:
 *   const tag = defineEntity('tag', z.object({ name: z.string(), color: z.string() }));
 *   // tag.routes.create.subject === 'cmd.tag.create'
 *   // z.infer<typeof tag.snapshot> → { id, tenantId, createdAt, updatedAt, name, color }
 *
 * @example With options (Session-style):
 *   const session = defineEntity('session', SessionDomain, {
 *     snapshotExtend: { openedAt: z.string().datetime() },
 *     errorCodes: extendCrudErrorCodes('tenant_mismatch'),
 *     createReplyExtra: { created: z.boolean() },
 *     list: {
 *       excludeFilterFields: ['data'],
 *       sortFields: ['openedAt'] as const,
 *     },
 *   });
 */
const defineEntity = (name, domain, opts = {}) => {
    const pascal = toPascalCase(name);
    const commands = (opts.commands ?? entity_ops_1.CRUD_COMMANDS);
    const events = (opts.events ?? entity_ops_1.CRUD_EVENTS);
    // Защита JS-потребителей и кастов: неизвестное имя операции — ошибка объявления.
    const assertKinds = (label, given, allowed) => {
        const unknownKinds = given.filter((kind) => !allowed.includes(kind));
        if (unknownKinds.length > 0) {
            throw new Error(`defineEntity('${name}'): unknown ${label} [${unknownKinds.join(', ')}]`);
        }
    };
    assertKinds('commands', commands, entity_ops_1.CRUD_COMMANDS);
    assertKinds('events', events, entity_ops_1.CRUD_EVENTS);
    // ── Snapshot / Patch ──
    // z.object({...spread}) instead of .extend() — .extend() produces extendShape<>
    // conditional mapped type that TypeScript collapses to {[x:string]:never} in .d.ts
    // when type arguments are generic. Simple intersection S & P & E works.
    const baseSnapshot = (0, compose_1.composeSnapshot)(domain);
    const snapshotShape = {
        ...baseSnapshot.shape,
        ...(opts.snapshotExtend ?? {}),
    };
    const snapshot = zod_1.z.object(snapshotShape);
    const patch = (0, compose_1.composePatch)(domain);
    // ── Projection (CRUDPROJ) ──
    // Whitelist проекции = ВСЕ ключи snapshot (не filterFields: context исключён
    // из фильтра, но проецируем именно его). Дот-пути — только в jsonb-поля.
    const projectableKeys = Object.keys(snapshotShape);
    const jsonbProjectionKeys = projectableKeys.filter((key) => (0, projection_zod_1.isProjectionJsonbSchema)(snapshotShape[key]));
    // Cast: runtime-схема валидирует точно (голова ∈ snapshot, путь ∈ jsonb) и
    // нормализует пустоту (null/[] → undefined, см. crudProjectionField); TS-тип
    // элемента — typed union по snapshot (см. CrudProjectionFieldOf). `.optional()`
    // уже внутри — переиспользуем один инстанс на fields/fieldsOmit в get и list
    // (preprocess без состояния, parse не мутирует).
    const projectionField = (0, projection_zod_1.crudProjectionField)(projectableKeys, jsonbProjectionKeys);
    const errorCodes = (opts.errorCodes ?? error_codes_1.CrudErrorCodes);
    const clientShape = (0, compose_1.extractClientShape)(domain.shape);
    // Платформенные таймстемпы доступны для filter/sort/groupBy у ВСЕХ сущностей
    // (естественное ожидание CRUD-стандарта: фильтр/группировка/сортировка по времени
    // создания/обновления). Колонки created_at/updated_at есть у каждой CRUD-таблицы,
    // fieldMapping их мапит — поэтому это безопасно и аддитивно (новых полей-поверхностей
    // не появляется, существующие запросы остаются валидными).
    const PLATFORM_TIME_FIELDS = ['createdAt', 'updatedAt'];
    // `id` — тоже платформенный и фильтруемый у всех (ROENT): «эти записи по списку id»
    // (`in`) и «одна запись» в общем запросе без отдельного get. В сортировку не входит.
    const PLATFORM_FILTER_FIELDS = ['id', ...PLATFORM_TIME_FIELDS];
    const dedupe = (arr) => [...new Set(arr)];
    const domainKeys = Object.keys(domain.shape);
    const sortFields = dedupe([
        ...(opts.list?.sortFields ?? domainKeys),
        ...PLATFORM_TIME_FIELDS,
    ]);
    const filterFields = dedupe([
        ...(0, extract_fields_1.extractFilterFields)(domain, { exclude: opts.list?.excludeFilterFields }),
        ...PLATFORM_FILTER_FIELDS,
    ]);
    const arrayFilterFields = opts.list?.arrayFields ?? [];
    for (const field of arrayFilterFields) {
        if (!filterFields.includes(field)) {
            throw new Error(`defineEntity('${name}'): list.arrayFields '${field}' — не фильтруемое поле`);
        }
        const fieldSchema = domain.shape[field];
        if (!fieldSchema || (0, projection_zod_1.unwrapSchemaTypeName)(fieldSchema) !== 'ZodArray') {
            throw new Error(`defineEntity('${name}'): list.arrayFields '${field}' — не z.array(...)`);
        }
    }
    const filterSchema = (0, filter_zod_1.crudFilterSchema)(filterFields, opts.list?.filterOps, {}, arrayFilterFields);
    // ── Command schemas ──
    const createPayloadShape = (opts.createPayload?.shape ?? clientShape);
    const createCommand = contracts_core_1.CommandMessage.extend({
        type: zod_1.z.literal(`${pascal}CreateCommand`),
        version: zod_1.z.literal(1),
        ...createPayloadShape,
    });
    const createReply = (0, reply_helpers_1.crudCreateReplyBody)(snapshot, errorCodes, opts.createReplyExtra);
    const deleteCommand = contracts_core_1.CommandMessage.extend({
        type: zod_1.z.literal(`${pascal}DeleteCommand`),
        version: zod_1.z.literal(1),
        id: id_1.EntityId,
    });
    const deleteReply = (0, reply_helpers_1.crudAckReplyBody)(errorCodes);
    // ── Query schemas ──
    const getQuery = contracts_core_1.QueryMessage.extend({
        type: zod_1.z.literal(`${pascal}GetQuery`),
        version: zod_1.z.literal(1),
        id: id_1.EntityId,
        fields: projectionField,
        fieldsOmit: projectionField,
    }).superRefine(projection_zod_1.crudProjectionXorRefine);
    // Wire-терпимость к проекции: partial + обязательные id/tenantId. TS-тип
    // остаётся полным snapshot (утверждённая асимметрия O4, см.
    // docs/crud-spec.md раздел Projection) — честный Partial каскадно ломает
    // всех существующих потребителей list/get.
    // I2: ZodObject-типизированные jsonb-поля в wire-форме заменяем на терпимый
    // record — shallow `.partial()` не ослабляет ИХ вложенные required-ключи, а
    // проекция путей (`fields:['scheduleJson.tz']`) отдаёт поддеревья. TS-тип уже
    // кастуется (O4) — асимметрия та же.
    const looseJsonbShape = Object.fromEntries(jsonbProjectionKeys
        .filter((key) => (0, projection_zod_1.isProjectionObjectSchema)(snapshotShape[key]))
        // `.nullish()` — поле могло быть nullable/optional в snapshot: терпимая
        // wire-форма не должна стать строже исходной.
        .map((key) => [key, zod_1.z.record(zod_1.z.unknown()).nullish()]));
    const wireItem = snapshot
        .partial()
        .required({ id: true, tenantId: true })
        .extend(looseJsonbShape);
    const getReply = (0, reply_helpers_1.crudGetReplyBody)(wireItem);
    const maxLimit = opts.list?.maxLimit ?? 10_000;
    const sortEntrySchema = zod_1.z.object({
        field: zod_1.z.enum(sortFields),
        dir: zod_1.z.enum(['asc', 'desc']),
    });
    const typedFilterSchema = filterSchema;
    const listQuery = contracts_core_1.QueryMessage.extend({
        type: zod_1.z.literal(`${pascal}ListQuery`),
        version: zod_1.z.literal(1),
        filter: typedFilterSchema.optional(),
        limit: zod_1.z.number().int().positive().max(maxLimit).optional(),
        offset: zod_1.z.number().int().nonnegative().optional(),
        sort: zod_1.z.array(sortEntrySchema).min(1).optional(),
        fields: projectionField,
        fieldsOmit: projectionField,
    }).superRefine(projection_zod_1.crudProjectionXorRefine);
    const listReply = (0, reply_helpers_1.crudListReplyBody)(wireItem, errorCodes);
    // ── Update (CRUDCAS) ──
    // `where` — условие записи: тот же filter-DSL и те же поля, что у list
    // (новой грамматики нет, O1). Без `where` — прежняя безусловная запись.
    const updateCommand = contracts_core_1.CommandMessage.extend({
        type: zod_1.z.literal(`${pascal}UpdateCommand`),
        version: zod_1.z.literal(1),
        id: id_1.EntityId,
        patch,
        where: typedFilterSchema.optional(),
    });
    const updateReply = (0, reply_helpers_1.crudUpdateReplyBody)(wireItem, errorCodes);
    // ── Aggregate query (q.<name>.aggregate) ──
    // Девятый стандартный route: groupBy/aggr/having по тем же filterFields,
    // что list (тех же данных он и касается — новой поверхности не экспонирует).
    // superRefine — cross-field инварианты алиасов (см. crudAggregateRefine).
    const typedAggregateShape = (0, aggregate_zod_1.crudAggregateShape)(filterFields, typedFilterSchema, opts.aggregate?.limits);
    const aggregateQuery = contracts_core_1.QueryMessage.extend({
        type: zod_1.z.literal(`${pascal}AggregateQuery`),
        version: zod_1.z.literal(1),
        ...typedAggregateShape,
    }).superRefine(aggregate_zod_1.crudAggregateRefine);
    const aggregateReply = (0, reply_helpers_1.crudAggregateReplyBody)(errorCodes);
    // ── Lookup (q.<name>.lookup) — только по опции ──
    const lookupConfig = opts.lookup
        ? {
            fields: opts.lookup.fields,
            searchFields: (0, lookup_zod_1.resolveLookupSearchFields)(snapshotShape, opts.lookup.fields, opts.lookup.searchFields, name),
            maxLimit: opts.lookup.maxLimit ?? lookup_zod_1.LOOKUP_DEFAULT_MAX_LIMIT,
            ...(opts.lookup.derived
                ? {
                    derived: (0, lookup_zod_1.resolveLookupDerived)(snapshotShape, opts.lookup.fields, opts.lookup.derived, name),
                }
                : {}),
        }
        : undefined;
    // Схемы строятся всегда (дёшево), но в routes route попадает только при опции lookup.
    const lookupItem = (lookupConfig
        ? (0, lookup_zod_1.crudLookupItemSchema)(snapshotShape, lookupConfig.fields, name).extend(opts.lookup?.derived?.shape ?? {})
        : zod_1.z.object({ id: id_1.EntityId }));
    const lookupQuery = contracts_core_1.QueryMessage.extend({
        type: zod_1.z.literal(`${pascal}LookupQuery`),
        version: zod_1.z.literal(1),
        ...(0, lookup_zod_1.crudLookupQueryShape)(lookupConfig?.maxLimit ?? lookup_zod_1.LOOKUP_DEFAULT_MAX_LIMIT),
    });
    const lookupReply = (0, lookup_zod_1.crudLookupReplyBody)(lookupItem);
    const lookupRoute = (0, define_crud_1.defineQueryLookup)(`q.${name}.lookup`, lookupQuery, lookupReply);
    // ── Event schemas ──
    const entityField = { [name]: snapshot };
    const createdEvent = contracts_core_1.EventMessage.extend({
        type: zod_1.z.literal(`${pascal}CreatedEvent`),
        version: zod_1.z.literal(1),
    }).extend(entityField);
    const updatedEvent = contracts_core_1.EventMessage.extend({
        type: zod_1.z.literal(`${pascal}UpdatedEvent`),
        version: zod_1.z.literal(1),
    }).extend(entityField);
    const deletedEvent = contracts_core_1.EventMessage.extend({
        type: zod_1.z.literal(`${pascal}DeletedEvent`),
        version: zod_1.z.literal(1),
        id: id_1.EntityId,
    });
    // ── Routes ──
    const baseRoutes = {
        create: (0, define_crud_1.defineCommandCreate)(`cmd.${name}.create`, createCommand, createReply),
        update: (0, define_crud_1.defineCommandUpdate)(`cmd.${name}.update`, updateCommand, updateReply),
        delete: (0, define_crud_1.defineCommandDelete)(`cmd.${name}.delete`, deleteCommand, deleteReply),
        get: (0, define_crud_1.defineQueryGet)(`q.${name}.get`, getQuery, getReply),
        list: (0, define_crud_1.defineQueryListBounded)(`q.${name}.list`, listQuery, listReply),
        aggregate: (0, define_crud_1.defineQueryAggregate)(`q.${name}.aggregate`, aggregateQuery, aggregateReply),
        created: (0, define_crud_1.defineEventCreated)(`evt.${name}.created`, createdEvent),
        updated: (0, define_crud_1.defineEventUpdated)(`evt.${name}.updated`, updatedEvent),
        deleted: (0, define_crud_1.defineEventDeleted)(`evt.${name}.deleted`, deletedEvent),
    };
    const presentKeys = [...entity_ops_1.CRUD_READS, ...commands, ...events];
    const presentRoutes = Object.fromEntries(Object.entries(baseRoutes).filter(([key]) => presentKeys.includes(key)));
    const routes = (lookupConfig ? { ...presentRoutes, lookup: lookupRoute } : presentRoutes);
    return {
        name,
        domain,
        /** Объявленные команды записи (метаданные для генераторов/рантайма; на проводе — `routes`). */
        commands,
        /** Объявленные события. */
        events,
        snapshot,
        patch,
        filterFields,
        /** Поля-массивы с операторами `any_*` (`list.arrayFields`); читают crud-runtime и генератор доки. */
        arrayFilterFields,
        filterSchema,
        /** Конфиг справочного чтения (`undefined` — опции `lookup` нет). Читает CrudNatsHandlers. */
        lookup: lookupConfig,
        types: {},
        filterLeaf: {},
        routes,
        schemas: {
            createCommand,
            createReply,
            updateCommand,
            updateReply,
            deleteCommand,
            deleteReply,
            getQuery,
            getReply,
            listQuery,
            listReply,
            aggregateQuery,
            aggregateReply,
            lookupQuery,
            lookupReply,
            createdEvent,
            updatedEvent,
            deletedEvent,
        },
    };
};
exports.defineEntity = defineEntity;
//# sourceMappingURL=define-entity.js.map